import * as React from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import queryString from 'query-string';
import { logout as logoutAction } from '../../actions/auth';
import * as McpApi from '../../api/mcp';
import { BRAND_NAME } from '../../logic/brand';
import ConsentLayout from '../../components/Authorize/consent-layout';
import ConsentCard from '../../components/Authorize/consent-card';
import ConsentStateCard, { LoadingCard } from '../../components/Authorize/consent-state-card';

// Screen state an error of the consent endpoints leads to. Anything the user
// can retry (network, 5xx, 400, an unknown 403) is 'network'.
export function stateForError(error) {
  const response = error && error.response;

  if (!response) {
    return 'network';
  }

  const code = response.data && response.data.error;

  switch (response.status) {
    case 401:
      return 'unauthorized';
    case 404:
      return 'expired';
    case 409:
      return 'decided';
    case 403:
      if (code === 'PROFILE_NOT_APPROVED') {
        return 'notApproved';
      }

      return code === 'ACCOUNT_UNAVAILABLE' ? 'unavailable' : 'network';
    default:
      return 'network';
  }
}

// The request id is an opaque identifier: used only to call the API, never
// logged, never turned into a URL by the front.
function readRequestId(search) {
  const { request } = queryString.parse(search);

  return typeof request === 'string' && request !== '' ? request : null;
}

function isPendingRequest(data) {
  return !!data && !!data.client && !!data.account && Array.isArray(data.scopes) && data.scopes.length > 0;
}

export default function Authorize({ loggedIn, user, logout }) {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const requestId = readRequestId(location.search);
  const [view, setView] = React.useState(() => ({ kind: requestId ? 'loading' : 'expired' }));
  const [sending, setSending] = React.useState(null);
  const [actionError, setActionError] = React.useState(false);
  const [attempt, setAttempt] = React.useState(0);
  const mounted = React.useRef(true);

  React.useEffect(() => () => {
    mounted.current = false;
  }, []);

  // Logged out (on arrival, after a 401 or after "switch account"): log in
  // first; PublicOnlyRoute brings the user back to this exact URL. The return
  // path travels in the history state, never in the query string.
  React.useEffect(() => {
    if (!loggedIn) {
      history.replace('/login', { from: { pathname: location.pathname, search: location.search } });
    }
  }, [loggedIn, history, location.pathname, location.search]);

  // A 401 means the session is gone: log out locally (the login page then
  // shows "session expired") and let the effect above go to the login page.
  const handleError = React.useCallback((error) => {
    const next = stateForError(error);

    if (next === 'unauthorized') {
      dispatch(logoutAction(true));

      return null;
    }

    return next;
  }, [dispatch]);

  React.useEffect(() => {
    if (!loggedIn || !requestId) {
      return undefined;
    }

    let active = true;

    setView({ kind: 'loading' });
    McpApi.getAuthorizationRequest(requestId)
      .then(({ data }) => {
        if (active) {
          setView(isPendingRequest(data) ? { kind: 'ready', request: data } : { kind: 'expired' });
        }
      })
      .catch((error) => {
        if (!active) {
          return;
        }

        const next = handleError(error);

        if (next) {
          setView({ kind: next });
        }
      });

    return () => {
      active = false;
    };
  }, [loggedIn, requestId, attempt, handleError]);

  const decide = (decision, call) => {
    setSending(decision);
    setActionError(false);

    call()
      .then(({ data }) => {
        const redirectTo = data && data.redirect_to;

        if (typeof redirectTo === 'string' && redirectTo !== '') {
          // The only way back to the client: the URL the backend built for this
          // decision. "Sending" stays on while the browser leaves the page.
          window.location.assign(redirectTo);

          return;
        }

        if (mounted.current) {
          setSending(null);
          setActionError(true);
        }
      })
      .catch((error) => {
        if (!mounted.current) {
          return;
        }

        setSending(null);

        const next = handleError(error);

        if (next === 'network') {
          setActionError(true);
        } else if (next) {
          setView({ kind: next });
        }
      });
  };

  const approve = (scopes) => decide('approve', () => McpApi.approveAuthorizationRequest(requestId, scopes));
  const deny = () => decide('deny', () => McpApi.denyAuthorizationRequest(requestId));
  // Logging out leads to the login page through the logged-out effect above,
  // so nothing navigates before the session is really gone. Until then the
  // screen stays "sending": no decision can go out with the old session.
  const switchAccount = () => {
    setSending('switch');
    logout();
  };

  if (!loggedIn) {
    return null;
  }

  let content;

  if (view.kind === 'loading') {
    content = <LoadingCard />;
  } else if (view.kind === 'ready') {
    content = (
      <ConsentCard
        request={view.request}
        brandName={BRAND_NAME}
        sending={sending}
        actionError={actionError}
        onApprove={approve}
        onDeny={deny}
        onSwitchAccount={switchAccount}
      />
    );
  } else {
    content = (
      <ConsentStateCard
        kind={view.kind}
        account={{ name: [user.name, user.surname].filter(Boolean).join(' '), email: user.email }}
        brandName={BRAND_NAME}
        sending={sending}
        actionError={actionError}
        onRetry={() => setAttempt((value) => value + 1)}
        onDeny={deny}
        onSwitchAccount={switchAccount}
      />
    );
  }

  return <ConsentLayout brandName={BRAND_NAME}>{content}</ConsentLayout>;
}
